import {
  AssetCondition,
  CurrencyCodeEnum,
} from '@electricjs/core_entity-client';
import {
  Link,
  Flex,
  IconInCircle,
  Text,
  ColorsV2,
  useThemeColors,
  Box,
  Banner,
  spaces,
  Tooltip,
  COUNTRIES,
  Country,
} from '@electricjs/arc';
import StyledRouterLink from '@/components/StyledRouterLink';
import { useTranslation } from 'react-i18next';

import { OutlineChip } from '@common';
import { getAssetConditionDisplayNameMap } from '@/components/Assets/helpers/transformOptions';
import { Site } from '@/types/assets';
import formatCurrency from '@/helpers/formatCurrency';
import formatDate from '@/helpers/formatDate';
import { getCountryName } from '@/helpers/getCountryName';
import getProductTypeIcon from '@/components/Assets/helpers/getProductTypeIcon';

import { emptyValue } from '@/constants/common';
import { ProductType } from '@/types/products';
import { useStorefront } from '../../StorefrontProvider';

const CountryChip = ({ countryCode }: { countryCode: Country }) => {
  const [bgColor, textColor, outlineColor] = useThemeColors([
    ColorsV2.WARNING_LIGHTEST,
    ColorsV2.WARNING_DARKEST,
    ColorsV2.WARNING_LIGHTEST,
  ]);

  return (
    <OutlineChip
      id="asset-site-country-chip"
      label={`${COUNTRIES[countryCode]?.flag} ${getCountryName(countryCode)}`}
      backgroundColor={bgColor}
      outlineColor={outlineColor}
      textColor={textColor}
      variant="small"
    />
  );
};

const SelfShipChip = () => {
  const { t } = useTranslation(['common']);
  const [bgColor, foregroundColor] = useThemeColors([
    ColorsV2.WARNING_LIGHTEST,
    ColorsV2.WARNING_DARKEST,
  ]);
  return (
    <Tooltip
      content={t('storefront.asset.shippingFromCustomerSitesWarning')}
      maxWidth={'300px'}>
      <div>
        <OutlineChip
          id="asset-self-ship-required-chip"
          label={t('storefront.asset.selfShipChipLabel')}
          backgroundColor={bgColor}
          outlineColor={foregroundColor}
          textColor={foregroundColor}
          icon="information-circle"
          variant="small"
        />
      </div>
    </Tooltip>
  );
};

type AssetDescriptionProps = {
  customAssetId: string;
  name: string;
  assetId?: string;
  onNavigate?: () => void;
  brand?: string;
  model?: string;
  serialNumber?: string;
  condition?: AssetCondition;
  isTextBold?: boolean;
  isTextTruncated?: boolean;
  fullDescription?: boolean;
  purchaseDate?: string;
  purchasePrice?: number;
  purchasePriceCurrencyCode?: CurrencyCodeEnum;
  purchasedFromUrl?: string;
  notes?: string;
  site?: Site | null;
  productType?: ProductType;
  isShippable?: boolean;
  selfShipRequired?: boolean;
};

const AssetDescription = ({
  customAssetId,
  name,
  assetId,
  onNavigate,
  brand,
  model,
  serialNumber,
  condition,
  isTextBold = true,
  isTextTruncated = true,
  fullDescription = false,
  purchaseDate,
  purchasePrice,
  purchasePriceCurrencyCode,
  purchasedFromUrl,
  notes,
  site,
  productType,
  isShippable,
  selfShipRequired,
}: AssetDescriptionProps) => {
  const { t } = useTranslation(['common', 'assets']);
  const [backgroundColor] = useThemeColors([ColorsV2.PRIMARY_LIGHTEST]);
  const assetConditionDisplayNameMap = getAssetConditionDisplayNameMap(t);
  const updatedCondition = condition && assetConditionDisplayNameMap[condition];

  const isThereDeliveryFee = isShippable && !selfShipRequired;

  const { storefrontCheckoutForm } = useStorefront();
  const shoppingCountryCode =
    storefrontCheckoutForm?.getValues('shoppingCountry')?.value;

  const siteCountryCode = site?.address?.country as Country;

  const isDisabled = isShippable === false;

  const isStoredInDifferentCountry =
    Boolean(siteCountryCode) && siteCountryCode !== shoppingCountryCode;

  return (
    <Flex
      vertical
      rowGap="1rem"
      m={fullDescription ? '2rem 0' : '2rem 0 3rem 0'}
      p={fullDescription ? '0rem 4rem' : undefined}>
      {fullDescription ? (
        <Flex columnGap="4rem" mb="2rem" alignItems="center">
          <IconInCircle
            intent="primary"
            background={backgroundColor}
            icon={productType ? getProductTypeIcon(productType.name) : 'laptop'}
            size="8rem"
            iconSize="large"
            smoothedBorder
          />
          <Flex vertical>
            <Text
              variant={isTextBold ? 'label-large' : undefined}
              truncateAt={isTextTruncated ? '100%' : undefined}
              title={name}
              intent={isDisabled ? 'disabled' : undefined}
              mb="1rem">
              {assetId ? (
                <StyledRouterLink
                  to={`/assets/${assetId}`}
                  onClick={onNavigate}>
                  {name}
                </StyledRouterLink>
              ) : (
                name
              )}
            </Text>
            <Flex gap={spaces.space_xs_rem} alignItems="center">
              <Text intent={isDisabled ? 'disabled' : undefined}>
                {t('storefront.asset.site')}: {site ? site.name : emptyValue}
              </Text>
              {isStoredInDifferentCountry && (
                <CountryChip countryCode={siteCountryCode} />
              )}
              {selfShipRequired && <SelfShipChip />}
              {isThereDeliveryFee && (
                <OutlineChip
                  id="warehouse-shipping-cost-chip"
                  label={t('storefront.asset.shippingStartingAt')}
                  variant="small"
                />
              )}
            </Flex>
          </Flex>
        </Flex>
      ) : (
        <Text
          variant={isTextBold ? 'label-large' : undefined}
          intent={isDisabled ? 'disabled' : undefined}
          truncateAt={isTextTruncated ? '100%' : undefined}
          title={name}
          mb="1rem">
          {name}
        </Text>
      )}
      <Text intent={isDisabled ? 'disabled' : undefined}>
        {t('storefront.asset.brand')}: {brand ? brand : emptyValue}
      </Text>
      <Text intent={isDisabled ? 'disabled' : undefined}>
        {t('storefront.asset.model')}: {model ? model : emptyValue}
      </Text>
      <Text intent={isDisabled ? 'disabled' : undefined}>
        {t('storefront.asset.serial')}:{' '}
        {serialNumber ? serialNumber : emptyValue}
      </Text>
      {fullDescription && (
        <Text intent={isDisabled ? 'disabled' : undefined}>
          {t('storefront.asset.assetId')}: {customAssetId}
        </Text>
      )}
      <Text intent={isDisabled ? 'disabled' : undefined}>
        {t('storefront.asset.condition')}: {updatedCondition ?? emptyValue}
      </Text>
      {!fullDescription && (
        <>
          <Flex alignItems={'center'} gap={spaces.space_xs_rem}>
            <Text intent={isDisabled ? 'disabled' : undefined}>
              {t('storefront.asset.site')}: {site ? site.name : emptyValue}
            </Text>
            {isStoredInDifferentCountry && (
              <CountryChip countryCode={siteCountryCode} />
            )}
          </Flex>
          {isThereDeliveryFee && (
            <Box m="-0.5rem 0 -2rem 0">
              <OutlineChip
                id="warehouse-shipping-cost-chip"
                label={t('storefront.asset.shippingStartingAt')}
                variant="small"
              />
            </Box>
          )}
        </>
      )}
      {selfShipRequired && !fullDescription && <SelfShipChip />}
      {isDisabled && isStoredInDifferentCountry && !fullDescription && (
        <Banner
          id="asset-stored-other-country-banner"
          isFlat
          width={'99%'}
          intent="warning"
          message={t('storefront.asset.storedInOtherCountryWarning', {
            country: getCountryName(siteCountryCode),
          })}
        />
      )}
      {fullDescription && (
        <>
          <Flex vertical rowGap="1rem" m="2rem 0rem">
            <Text intent={isDisabled ? 'disabled' : undefined}>
              {t('storefront.asset.purchaseDate')}:{' '}
              {purchaseDate ? formatDate(purchaseDate) : emptyValue}
            </Text>
            <Text intent={isDisabled ? 'disabled' : undefined}>
              {t('storefront.asset.purchasePrice')}:{' '}
              {purchasePrice
                ? formatCurrency(purchasePrice, purchasePriceCurrencyCode)
                : emptyValue}
            </Text>
            <Text intent={isDisabled ? 'disabled' : undefined}>
              {t('storefront.asset.purchasedFrom')}:{' '}
              {purchasedFromUrl ? (
                <Link
                  href={purchasedFromUrl}
                  rel="noopener noreferrer"
                  target="_blank">
                  {purchasedFromUrl}
                </Link>
              ) : (
                emptyValue
              )}
            </Text>
          </Flex>
          <Text intent={isDisabled ? 'disabled' : undefined}>
            {t('storefront.asset.notes')}: {notes ? notes : emptyValue}
          </Text>
        </>
      )}
    </Flex>
  );
};

export default AssetDescription;
